'use client';

import { useEffect, useState } from 'react';
import { Check, User, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import userDataStore, { type SubordinateEmployee } from '@/lib/offline/userDataStore';

interface TaskAssigneeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (assigneeIds: number[]) => void;
}

interface AssigneeOption {
  id: number;
  name: string;
  email: string | null;
  isSelf: boolean;
  subordinate?: SubordinateEmployee;
}

export default function TaskAssigneeModal({ open, onOpenChange, onConfirm }: TaskAssigneeModalProps) {
  const { user } = useAuth();
  const [subordinates, setSubordinates] = useState<SubordinateEmployee[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user?.client_id || !user?.id) {
      setSubordinates([]);
      return;
    }

    const loadSubordinates = async () => {
      setLoading(true);
      try {
        // Always use fresh data from user object (from latest /me response)
        // The offline store may have stale data without user_id
        let subordinatesData: SubordinateEmployee[] = [];

        if (user.subordinates) {
          subordinatesData = user.subordinates;
          // Update offline store with fresh data
          try {
            await userDataStore.upsertUserData(
              Number(user.client_id),
              user.id,
              {
                user_id: user.id,
                employee_id: user.employee_id ?? null,
                subordinates: user.subordinates,
                updated_at: new Date().toISOString(),
              }
            );
          } catch (storeError) {
            console.warn('Failed to update offline store with fresh subordinates:', storeError);
          }
        } else {
          // Fallback to offline store if user object doesn't have subordinates
          const userData = await userDataStore.getUserData(
            Number(user.client_id),
            user.id
          );
          if (userData?.subordinates) {
            subordinatesData = userData.subordinates;
          }
        }

        setSubordinates(subordinatesData);
      } catch (error) {
        console.error('Failed to load subordinates:', error);
        // Fallback to user object on error
        if (user.subordinates) {
          setSubordinates(user.subordinates);
        } else {
          setSubordinates([]);
        }
      } finally {
        setLoading(false);
      }
    };

    void loadSubordinates();
  }, [open, user?.client_id, user?.id, user?.subordinates, user?.employee_id]);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedAssignees(user?.id ? [user.id] : []);
    }
  }, [open, user?.id]);

  const assigneeOptions: AssigneeOption[] = [
    {
      id: user?.id ?? 0,
      name: user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.email || 'Me',
      email: user?.email || null,
      isSelf: true,
    },
    ...subordinates.map((sub) => ({
      id: sub.id, // id is now user_id from backend
      name: sub.preferred_name || sub.first_name
        ? `${sub.preferred_name || sub.first_name} ${sub.last_name || ''}`.trim()
        : sub.email || 'Unknown',
      email: sub.email,
      isSelf: false,
      subordinate: sub,
    })),
  ];

  const handleConfirm = () => {
    if (selectedAssignees.length > 0) {
      onConfirm(selectedAssignees);
      onOpenChange(false);
    }
  };

  const toggleAssignee = (assigneeId: number) => {
    setSelectedAssignees(prev => {
      if (prev.includes(assigneeId)) {
        // Don't allow deselecting if it's the only selected assignee
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== assigneeId);
      }
      return [...prev, assigneeId];
    });
  };

  const hasSubordinates = subordinates.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white shadow-2xl rounded-xl border border-gray-200">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Assign Task
          </DialogTitle>
          <DialogDescription>
            {hasSubordinates
              ? 'Select who this task should be assigned to. You can assign it to yourself or one or more of your team members.'
              : 'This task will be assigned to you.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading team members...
          </div>
        ) : (
          <div className="space-y-2 py-4">
            {assigneeOptions.map((option) => (
              <div
                key={option.id}
                className={`
                  flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                  ${selectedAssignees.includes(option.id)
                    ? 'bg-primary/10 border-primary'
                    : 'hover:bg-muted/50 border-border'
                  }
                `}
                onClick={() => toggleAssignee(option.id)}
              >
                <div className={`
                  w-5 h-5 rounded border-2 flex items-center justify-center
                  ${selectedAssignees.includes(option.id)
                    ? 'bg-primary border-primary'
                    : 'border-gray-300'
                  }
                `}>
                  {selectedAssignees.includes(option.id) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${option.isSelf ? 'bg-primary/20' : 'bg-muted'}
                  `}>
                    {option.isSelf ? (
                      <User className="w-5 h-5 text-primary" />
                    ) : (
                      <Users className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {option.name}
                      {option.isSelf && (
                        <span className="ml-2 text-xs text-muted-foreground">(You)</span>
                      )}
                    </div>
                    {option.email && (
                      <div className="text-xs text-muted-foreground truncate">
                        {option.email}
                      </div>
                    )}
                  </div>
                  {selectedAssignees.includes(option.id) && (
                    <Check className="w-5 h-5 text-primary" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedAssignees.length === 0 || loading}
          >
            Assign Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
